import { StorageProvider, StorageProviderConfig, StorageProviderFactory } from './types';
import { SQLJSProvider } from './SQLJSProvider';
import { SQLJSWorkerProvider } from './SQLJSWorkerProvider';
import { initTracker } from './initTracker';

export type StorageProviderType = 'sqljs';

/**
 * Manages storage providers and provides a factory for creating them
 */
export class StorageManager {
    private static providers: Map<StorageProviderType, StorageProviderFactory> = new Map([
        ['sqljs', (config?: StorageProviderConfig) => new SQLJSWorkerProvider(config)]
    ]);

    private static currentProvider: StorageProvider | null = null;
    private static currentType: StorageProviderType = 'sqljs';
    private static initializationPromise: Promise<StorageProvider> | null = null;

    /**
     * Register a new storage provider type
     */
    static registerProvider(type: StorageProviderType, factory: StorageProviderFactory): void {
        this.providers.set(type, factory);
    }

    /**
     * Create a storage provider instance
     */
    static createProvider(type: StorageProviderType, config?: StorageProviderConfig): StorageProvider {
        const factory = this.providers.get(type);
        if (!factory) {
            throw new Error(`Unknown storage provider type: ${type}`);
        }
        return factory(config);
    }

    /**
     * Get the current storage provider instance (singleton pattern)
     */
    static async getProvider(): Promise<StorageProvider> {
        const state = {
            hasCurrentProvider: !!this.currentProvider,
            isInitialized: this.currentProvider ? (this.currentProvider as any).initialized : false,
            hasInitPromise: !!this.initializationPromise,
            currentType: this.currentType
        };

        initTracker.addEvent('info', 'StorageManager', 'getProvider called', state);

        // Check if we have a properly initialized provider
        if (this.currentProvider && (this.currentProvider as any).initialized) {
            initTracker.addEvent('success', 'StorageManager', 'Returning existing initialized provider', {
                provider: this.currentProvider.getName()
            });
            return this.currentProvider;
        }

        // Prevent race conditions by reusing the same initialization promise
        if (this.initializationPromise) {
            initTracker.addEvent('info', 'StorageManager', 'Waiting for existing initialization promise');
            return this.initializationPromise;
        }

        initTracker.addEvent('info', 'StorageManager', 'Starting new initialization', { type: this.currentType });
        this.initializationPromise = this.initializeProvider();
        return this.initializationPromise;
    }

    private static async initializeProvider(): Promise<StorageProvider> {
        try {
            initTracker.addEvent('info', 'StorageManager', `Creating ${this.currentType} provider`);
            this.currentProvider = this.createProvider(this.currentType);
            
            initTracker.addEvent('info', 'StorageManager', `Initializing ${this.currentProvider.getName()}`);
            await this.currentProvider.initialize();
            
            initTracker.addEvent('success', 'StorageManager', `Successfully initialized ${this.currentType} provider`, {
                provider: this.currentProvider.getName()
            });
            return this.currentProvider;
        } catch (error) {
            initTracker.addEvent('error', 'StorageManager', `Failed to initialize ${this.currentType} provider`, {
                error: error instanceof Error ? error.message : String(error)
            });
            
            // For now, don't fallback - let's see the actual error
            this.currentProvider = null;
            this.initializationPromise = null;
            throw new Error(`Failed to initialize storage provider (${this.currentType}): ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    /**
     * Switch to a different storage provider
     */
    static async switchProvider(type: StorageProviderType, config?: StorageProviderConfig): Promise<StorageProvider> {
        // Clean up current provider if needed
        if (this.currentProvider && this.currentProvider.getName() !== type) {
            // Could add cleanup logic here if needed
            this.currentProvider = null;
        }

        // Reset initialization state
        this.initializationPromise = null;
        this.currentType = type;
        
        try {
            this.currentProvider = this.createProvider(type, config);
            await this.currentProvider.initialize();
            return this.currentProvider;
        } catch (error) {
            this.currentProvider = null;
            throw new Error(`Failed to switch to storage provider (${type}): ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    /**
     * Get available storage provider types
     */
    static getAvailableProviders(): StorageProviderType[] {
        return Array.from(this.providers.keys());
    }

    /**
     * Get current provider type
     */
    static getCurrentProviderType(): StorageProviderType {
        return this.currentType;
    }
}